import type { App } from "@upsquad/server";
import type { CreateCommunityLinkPayload, UpdateCommunityLinkPayload } from "@upsquad/shared";
import type { EdenCall } from "@/src/common/lib/eden-base-query";
import { baseApi } from "@/src/common/services/base-api";

type ListLinksResponse = App["~Routes"]["community-link"][":communityId"]["get"]["response"]["200"];
type CreateLinkResponse = App["~Routes"]["community-link"][":communityId"]["post"]["response"]["200"];
type UpdateLinkResponse = App["~Routes"]["community-link"][":communityId"][":linkId"]["patch"]["response"]["200"];
type DeleteLinkResponse = App["~Routes"]["community-link"][":communityId"][":linkId"]["delete"]["response"]["200"];

interface ListLinksParams {
	communityId: string;
}

interface CreateLinkParams {
	communityId: string;
	payload: CreateCommunityLinkPayload;
}

interface UpdateLinkParams {
	communityId: string;
	linkId: string;
	payload: UpdateCommunityLinkPayload;
}

interface DeleteLinkParams {
	communityId: string;
	linkId: string;
}

export const communityLinkApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		listCommunityLinks: builder.query<ListLinksResponse, ListLinksParams>({
			query:
				({ communityId }): EdenCall<ListLinksResponse> =>
				(client) =>
					client["community-link"]({ communityId }).get(),
			providesTags: ["CommunityLinks"],
		}),

		createCommunityLink: builder.mutation<CreateLinkResponse, CreateLinkParams>({
			query:
				({ communityId, payload }): EdenCall<CreateLinkResponse> =>
				(client) =>
					client["community-link"]({ communityId }).post(payload),
			invalidatesTags: ["CommunityLinks"],
		}),

		updateCommunityLink: builder.mutation<UpdateLinkResponse, UpdateLinkParams>({
			query:
				({ communityId, linkId, payload }): EdenCall<UpdateLinkResponse> =>
				(client) =>
					client["community-link"]({ communityId })({ linkId }).patch(payload),
			invalidatesTags: ["CommunityLinks"],
		}),

		deleteCommunityLink: builder.mutation<DeleteLinkResponse, DeleteLinkParams>({
			query:
				({ communityId, linkId }): EdenCall<DeleteLinkResponse> =>
				(client) =>
					client["community-link"]({ communityId })({ linkId }).delete(),
			invalidatesTags: ["CommunityLinks"],
		}),
	}),
});

export const {
	useListCommunityLinksQuery,
	useCreateCommunityLinkMutation,
	useUpdateCommunityLinkMutation,
	useDeleteCommunityLinkMutation,
} = communityLinkApi;
